import priceServices from "../services/price";
import { toThousandFilter } from "./filters";
import store from "../store";
import { i18n } from "./i18n";
import RULES from "../config/rules";

const dayjs = require("dayjs").default;
const { DIGITS_RE } = RULES;

const defaultCpus = [1, 2, 4, 6, 8, 12, 16, 24, 32];
const defaultRams = [1, 2, 4, 6, 8, 12, 16, 24, 64, 128];
export const defaultDisks = [20, 40, 60];
export const defaultBandwidths = [100, 200, 300, 400];

export function getHoursOfMonth() {
  return dayjs().daysInMonth() * 24;
}

export function currency(value, currency = "", decimals = 2) {
  value = parseFloat(value);
  if (!isFinite(value) || isNaN(value)) return "";

  const [integerPart, fractionalPart] = value.toFixed(decimals).split(".");
  const formattedIntegerPart = integerPart.replace(DIGITS_RE, ".");

  return `${
    value < 0 ? "-" : ""
  }${currency}${formattedIntegerPart},${fractionalPart}`;
}

export function roundTo2(num) {
  return +(Math.round(num + "e+2") + "e-2");
}

export function formatPrice(value) {
  if (value === 0) {
    return "0,00 đ";
  }
  if (!value) {
    return "--";
  }
  if (value >= 1000000) {
    return `${roundTo2(parseFloat(value / 1000000))} tr`;
  }
  return `${currency(value)} đ`;
}

// Price flavor
export function formatParamCpu(category, planName, arrCpus = defaultCpus) {
  const params = arrCpus.map((cpu) => ({
    plan_name: planName,
    category_code: category,
    quantity: cpu,
  }));
  return params;
}

export function formatParamRam(category, planName, arrRams = defaultRams) {
  const params = arrRams.map((ram) => ({
    plan_name: planName,
    category_code: category,
    quantity: ram,
  }));
  return params;
}

export async function fetchPriceSpotInstance(flavors) {
  try {
    const params = flavors.map((flavor) => ({
      plan_name: flavor,
      quantity: 3600,
      category_code: "default",
    }));
    store.price.state.loadingPrice = true;
    const response = await priceServices.getPrice(params);
    const formatPrice = response?._items?.reduce((acc, item) => {
      const {
        plan_name: planName,
        category_code: categoryCode,
        ...rest
      } = item;
      acc[`${planName}_${categoryCode}`] =
        acc[`${planName}_${categoryCode}`] || [];
      acc[`${planName}_${categoryCode}`].push(rest);
      return acc;
    }, {});
    store.price.state.price = { ...store.price.state.price, ...formatPrice };
    store.price.state.loadingPrice = false;
  } catch (error) {
    store.price.state.loadingPrice = false;
  }
}

export async function fetchPriceCpuAndRam(billingPlan, category, flavors) {
  const suffix = billingPlan === "on_demand" ? "_on" : "";
  const planNameCpu = `cs:${billingPlan}_cpu${suffix}`;
  const planNameRam = `cs:${billingPlan}_ram${suffix}`;

  try {
    const params = [
      ...formatParamCpu(category, planNameCpu, flavors?.vcpus),
      ...formatParamRam(category, planNameRam, flavors?.ram),
    ];
    store.price.state.loadingPrice = true;
    const response = await priceServices.getPrice(params);
    const formatPrice = response?._items?.reduce((acc, item) => {
      const {
        plan_name: planName,
        category_code: categoryCode,
        ...rest
      } = item;
      acc[`${planName}_${categoryCode}`] =
        acc[`${planName}_${categoryCode}`] || [];
      acc[`${planName}_${categoryCode}`].push(rest);
      return acc;
    }, {});
    store.price.state.price = { ...store.price.state.price, ...formatPrice };
    store.price.state.loadingPrice = false;
  } catch (error) {
    store.price.state.loadingPrice = false;
  }
}

export function getCostFlavor({ category, cpu, ram, billingPlan }) {
  const suffix = billingPlan === "on_demand" ? "_on" : "";

  if (billingPlan === "spot_instance") {
    const currentFlavorName = `cs:${cpu}c_${ram}g_premium_spot_default`;
    const currentFlavor = store?.price?.state?.price[currentFlavorName];
    return currentFlavor?.[0]?.amount ?? 0;
  } else {
    const cpuName = `cs:${billingPlan}_cpu${suffix}_${category}`;
    const currentCpu = store?.price?.state?.price[cpuName];
    const costCpu = currentCpu?.find((item) => item?.quantity === cpu)?.amount;
    const ramName = `cs:${billingPlan}_ram${suffix}_${category}`;
    const currentRam = store?.price?.state?.price[ramName];
    const costRam = currentRam?.find((item) => item?.quantity === ram)?.amount;
    return costCpu + costRam ?? 0;
  }
}

export function getCostConfigHourly({ costFlavor, costDisk, billingPlan }) {
  if (billingPlan === "saving_plan") {
    return (costFlavor + costDisk) / getHoursOfMonth() ?? 0;
  }
  return (costFlavor + costDisk) * getHoursOfMonth() ?? 0;
}

export function calculationFlavorPrice({ cpu, ram, category, billingPlan }) {
  const tempPriceFlavor = getCostFlavor({ cpu, ram, category, billingPlan });
  const priceFlavor =
    billingPlan === "spot_instance" ? tempPriceFlavor / 3600 : tempPriceFlavor;
  const priceFlavorDisplay = `${toThousandFilter(priceFlavor)} đ/${i18n.t(
    billingPlan === "on_demand"
      ? "shared.hour"
      : billingPlan === "spot_instance"
      ? "shared.second"
      : "shared.month"
  )}`;
  return {
    priceFlavor,
    priceFlavorDisplay,
  };
}
// Price disk
function formatParamDisk(
  billingPlan,
  category,
  typeDisk,
  arrDisks = defaultDisks
) {
  const params = arrDisks.map((disk) => ({
    plan_name: `cs:${billingPlan}_${typeDisk}_disk`,
    category_code: category,
    quantity: disk,
  }));
  return params;
}

export async function fetchPriceDisk(
  billingPlan,
  category,
  typeDisk,
  arrDisks,
  planName
) {
  const newBillingPlan =
    billingPlan === "spot_instance" ? "on_demand" : billingPlan;
  try {
    const params = [
      ...formatParamDisk(newBillingPlan, category, typeDisk, arrDisks),
    ];
    store.price.state.loadingCustomPrice = true;
    const response = await priceServices.getPrice(params);
    if (!(planName in store.price.state.price)) {
      store.price.state.price[planName] = [...response?._items];
    } else {
      store.price.state.price[planName].push(...response?._items);
    }
    store.price.state.loadingCustomPrice = false;
  } catch (error) {
    store.price.state.loadingCustomPrice = false;
  }
}

export function getCostDisk({ size, type, billingPlan, category }) {
  if (
    (category === "vps" || category === "dedicated") &&
    billingPlan !== "saving_plan"
  ) {
    return;
  }
  if (!size || !type || !category) return 0;
  const newBillingPlan =
    billingPlan === "spot_instance" ? "on_demand" : billingPlan;
  const planName = `cs:${newBillingPlan}_${type}_disk`;
  const currentDisk = store?.price?.state?.price?.[planName]?.find(
    (disk) => disk?.quantity === size
  );
  if (!currentDisk && !store?.price?.state?.loadingCustomPrice) {
    fetchPriceDisk(newBillingPlan, category, type, [size], planName); // call api price khi volume chua co gia trong store
  }

  return currentDisk?.amount ?? 0;
}

export function getCostDiskHourly({ costDisk, billingPlan }) {
  if (billingPlan === "saving_plan") {
    return costDisk / getHoursOfMonth() ?? 0;
  }
  return costDisk * getHoursOfMonth() ?? 0;
}

export function calculationDiskPrice({ size, type, billingPlan, category }) {
  const priceDisk = getCostDisk({ size, type, billingPlan, category });
  const priceDiskDisplay = `${toThousandFilter(priceDisk)} đ/${i18n.t(
    billingPlan === "on_demand" || billingPlan === "spot_instance"
      ? "shared.hour"
      : "shared.month"
  )}`;
  return {
    priceDisk,
    priceDiskDisplay,
  };
}

// Price bandwidth
export async function fetchBandwidthPrice(
  arrBandwidths = defaultBandwidths,
  customCost = false
) {
  const { price } = store?.price?.state;
  const planName = "cs:bandwidth";
  if (Object.keys(price).includes(planName) && !customCost) return;

  try {
    const params = arrBandwidths.map((bandwidth) => ({
      plan_name: planName,
      category_code: "default",
      quantity: bandwidth,
    }));
    store.price.state.loadingCustomPrice = true;
    const response = await priceServices.getPrice(params);
    store.price.state.loadingCustomPrice = false;
    if (!Object.keys(price).includes(planName)) {
      price[planName] = [...response?._items];
    } else {
      price[planName].push(...response?._items);
    }
  } catch (error) {
    store.price.state.loadingCustomPrice = false;
  }
}
export function costBandwidth(size) {
  if (!size || size < 0) return 0;
  const planname = "cs:bandwidth";
  const currentBandwith = store?.price?.state?.price?.[planname]?.find(
    (bandwidth) => bandwidth?.quantity === size
  );
  if (!currentBandwith && !store.price.state.loadingCustomPrice) {
    fetchBandwidthPrice([size], true);
  }

  return currentBandwith?.amount ?? 0;
}

export function filterBandwidthPricing(size) {
  let price = "";
  const block = 1000;
  if (size / block === 100 || !size) {
    price = i18n.t("iaasCloud.quotas.free");
  } else if (size / block > 100) {
    price = formatPrice(costBandwidth(size / block - 100));
  }
  return price;
}

// Price Wanip
export async function fetchWanipPrice() {
  const { price } = store?.price?.state;
  const planName = "cs:wan_ip";
  if (Object.keys(price).includes(planName)) return;

  try {
    const params = {
      plan_name: planName,
      category_code: "default",
      quantity: 1,
    };
    store.price.state.loadingPrice = true;
    const response = await priceServices.getPrice(params);
    const formatPrice = {
      [planName]: response?._items,
    };
    store.price.state.price = { ...price, ...formatPrice };
    store.price.state.loadingPrice = false;
  } catch (error) {
    console.error(error);
    store.price.state.loadingPrice = false;
  }
}

// Price snapshot, backup
export async function fetchPriceSnapshotAndBackup(category, size, planName) {
  try {
    const params = {
      plan_name: planName,
      category_code: category,
      quantity: size,
    };
    store.price.state.loadingPrice = true;
    const response = await priceServices.getPrice(params);
    if (!(planName in store.price.state.price)) {
      store.price.state.price[planName] = [...response?._items];
    } else {
      store.price.state.price[planName].push(...response?._items);
    }
    store.price.state.loadingPrice = false;
  } catch (error) {
    store.price.state.loadingPrice = false;
  }
}

export function getCostSnapshotAndBackup({
  size,
  type,
  billingPlan,
  category,
  typeCost,
}) {
  const newBillingPlan =
    billingPlan === "spot_instance" ? "on_demand" : billingPlan;
  if (!size || !type || !category) return 0;
  const planName = `cs:${newBillingPlan}_${type}_${typeCost}`;
  const currentPlanName = store?.price?.state?.price?.[planName]?.find(
    (disk) => disk?.quantity === size
  );

  if (!currentPlanName && !store?.price?.state?.loadingPrice) {
    fetchPriceSnapshotAndBackup(category, size, planName);
  }

  return currentPlanName?.amount ?? 0;
}
